import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Loader2, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { useOptionalShowAndTell } from './useShowAndTell';

function noticeKey(userId: string | undefined): string {
  return `harmony:show-tell-notice:${userId ?? 'anonymous'}`;
}

/** localStorage is a per-viewer convenience: a blocked or empty store only means the notice shows again. */
function hasSeenNotice(userId: string | undefined): boolean {
  try {
    return window.localStorage.getItem(noticeKey(userId)) === '1';
  } catch {
    return false;
  }
}

function rememberNotice(userId: string | undefined): void {
  try {
    window.localStorage.setItem(noticeKey(userId), '1');
  } catch {
    // Never blocks starting a recording.
  }
}

type Refusal = 'mic-denied' | 'mic-missing' | 'mic-error';

/** The `consultantShowAndTell.start.refusal` key for each refusal the recorder can answer with. */
const REFUSAL_KEYS = { 'mic-denied': 'denied', 'mic-missing': 'missing', 'mic-error': 'error' } as const;

interface StartShowAndTellProps {
  /** Text already typed in the panel; saved with the recording as a note (F8). */
  note: string;
  /** Called after recording has begun, so the panel can close. */
  onStarted: () => void;
}

/** screens.md S1, option B: a clear primary button above the text box, plus the first-use notice. */
export function StartShowAndTell({ note, onStarted }: StartShowAndTellProps) {
  const t = useTranslations('consultantShowAndTell.start');
  const api = useOptionalShowAndTell();
  const { user } = useAuth();
  const [showNotice, setShowNotice] = useState(false);
  const [starting, setStarting] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const support = api?.support;
  if (!api || !support || ('reason' in support && support.reason === 'disabled')) return null;

  async function begin() {
    if (!api) return;
    setStarting(true);
    setRefusal(null);
    try {
      const outcome = await api.start(note);
      if (outcome === 'started') {
        rememberNotice(user?.id);
        onStarted();
      } else if (outcome === 'mic-denied' || outcome === 'mic-missing' || outcome === 'mic-error') {
        setRefusal(outcome);
        setShowNotice(false);
      }
    } finally {
      setStarting(false);
    }
  }

  function handleStartClick() {
    if (hasSeenNotice(user?.id)) void begin();
    else setShowNotice(true);
  }

  if (showNotice) {
    return (
      <div className="flex flex-col gap-3" data-testid="show-tell-notice">
        <h3 className="text-sm font-semibold">{t('notice.title')}</h3>
        <Alert>
          <AlertTitle>{t('notice.recordTitle')}</AlertTitle>
          <AlertDescription>{t('notice.recordBody')}</AlertDescription>
        </Alert>
        <Alert>
          <AlertTitle>{t('notice.notRecordTitle')}</AlertTitle>
          <AlertDescription>{t('notice.notRecordBody')}</AlertDescription>
        </Alert>
        <p className="text-xs text-muted-foreground">{t('notice.footnote')}</p>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={() => setShowNotice(false)} disabled={starting}>
            {t('notice.back')}
          </Button>
          <Button size="sm" onClick={() => void begin()} disabled={starting}>
            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
            {t('notice.start')}
          </Button>
        </div>
      </div>
    );
  }

  const unsupported = 'reason' in support ? support : null;

  return (
    <div className="flex flex-col gap-2">
      {refusal && (
        <Alert variant="destructive">
          <AlertTitle>{t(`refusal.${REFUSAL_KEYS[refusal]}.title`)}</AlertTitle>
          <AlertDescription>
            <div className="flex flex-col gap-2">
              <p>{t(`refusal.${REFUSAL_KEYS[refusal]}.body`)}</p>
              <Button variant="secondary" size="sm" className="self-start" onClick={() => setRefusal(null)}>
                {t('typeInstead')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
      <Button
        className="w-full"
        onClick={handleStartClick}
        disabled={!!unsupported || starting}
        aria-label={t('button')}
      >
        {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
        {t('button')}
      </Button>
      <p className="text-xs text-muted-foreground">
        {unsupported ? t('unsupported') : t('intro')}
      </p>
      {!unsupported && (
        <Button variant="link" size="sm" className="self-start h-auto p-0 text-xs" onClick={() => setShowNotice(true)}>
          {t('whatsRecorded')}
        </Button>
      )}
      <div className="flex items-center gap-2 text-xs text-muted-foreground" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        {t('or')}
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}
